import React from "react";
import { Box, Typography } from "@mui/material";
import { Link } from "react-router-dom";

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
}

interface DiaCircleProps {
  dia: number | string;
  gauge: string;
  machineType: string;
  machines?: Machine[];
  size?: "small" | "medium" | "large";
  disableLink?: boolean;
}

const DiaCircle: React.FC<DiaCircleProps> = ({
  dia,
  gauge,
  machineType,
  machines,
  size = "medium",
  disableLink = false,
}) => {
  // Find the machine ID based on dia, gauge, and machine_type
  const machine = machines
    ? machines.find(
        (m) =>
          Number(m.dia) === Number(dia) &&
          m.gauge === gauge &&
          m.machine_type === machineType
      )
    : undefined;

  // Determine colors based on gauge
  const getBackgroundColor = () => {
    switch (gauge) {
      case "14":
        return "#90caf9"; // Sky blue
      case "16":
        return "#00acc1"; // Cyan/teal
      case "24":
        return "#ff9e80"; // Coral/salmon
      default:
        return "#90caf9"; // Default to sky blue
    }
  };

  const backgroundColor = getBackgroundColor();
  const textColor = "#fff";

  // Size configurations
  const sizeConfig = {
    small: {
      width: 36,
      height: 36,
      fontSize: "0.875rem",
    },
    medium: {
      width: 48,
      height: 48,
      fontSize: "1rem",
    },
    large: {
      width: 60,
      height: 60,
      fontSize: "1.25rem",
    },
  };

  const { width, height, fontSize } = sizeConfig[size];

  const circleContent = (
    <Box
      sx={{
        width,
        height,
        borderRadius: "50%",
        backgroundColor,
        color: textColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        fontSize,
        transition: "all 0.2s ease-in-out",
        cursor: machine ? "pointer" : "default",
        "&:hover": machine
          ? {
              transform: "scale(1.1)",
              boxShadow: 3,
            }
          : {},
      }}
      onClick={(e) => {
        // Stop propagation even for non-linked circles to prevent row clicks
        if (!machine) {
          e.stopPropagation();
        }
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: "bold",
          fontSize,
          color: textColor,
        }}
      >
        {dia}
      </Typography>
    </Box>
  );

  // If machine found and link not disabled, wrap in Link
  if (machine && !disableLink) {
    return (
      <Link
        to={`/machines/${machine.id}`}
        style={{
          textDecoration: "none",
          display: "inline-block",
        }}
        onClick={(e) => {
          // Stop propagation to prevent parent row/card click handlers
          e.stopPropagation();
        }}
      >
        {circleContent}
      </Link>
    );
  }

  // If no machine found or link disabled, just return the circle without link
  return circleContent;
};

export default DiaCircle;
